import type {
  AdmissionRuling,
  AdmittedEntryResponse,
} from "@patent-studio/api-types";
import { Button } from "@/components/ui/button";
import { MicroLabel } from "@/components/ui/MicroLabel";
import { Panel } from "@/components/ui/Panel";
import { StatusPill } from "@/components/ui/StatusPill";
import type { Tone } from "@/components/ui/tone";

/** How each entry reads to the attorney: read as art until they strike it. */
const STATE: Record<"read" | AdmissionRuling, { label: string; tone: Tone }> = {
  read: { label: "Read as prior art", tone: "info" },
  accepted: { label: "Accepted as prior art", tone: "success" },
  struck: { label: "Struck: not read", tone: "neutral" },
};

/** One admission: its passage, what it says is known, and the two rulings. */
function AdmittedRow({
  entry,
  busy,
  onRule,
}: {
  entry: AdmittedEntryResponse;
  busy: boolean;
  onRule: (entryId: string, ruling: AdmissionRuling) => void;
}) {
  const state = STATE[entry.ruling ?? "read"];
  return (
    <Panel
      as="li"
      shape="row"
      pad="tight"
      data-testid={`admitted-${entry.entry_id}`}
      className="grid gap-1.5 text-detail"
    >
      <div className="flex flex-wrap items-center gap-2">
        <StatusPill tone={state.tone} size="sm" data-testid="admitted-state">
          {state.label}
        </StatusPill>
        <span className="text-muted-foreground">
          Known: {entry.known}
          {entry.cites_pages && ` · page ${String(entry.page)}`}
        </span>
      </div>
      <blockquote className="border-l-2 border-border pl-2.5 italic">
        {entry.quote}
      </blockquote>
      <div className="flex gap-2">
        {entry.ruling !== "accepted" && (
          <Button
            type="button"
            variant="secondary"
            size="dense"
            disabled={busy}
            onClick={() => {
              onRule(entry.entry_id, "accepted");
            }}
          >
            Accept
          </Button>
        )}
        {entry.ruling !== "struck" && (
          <Button
            type="button"
            variant="quiet"
            size="dense"
            disabled={busy}
            onClick={() => {
              onRule(entry.entry_id, "struck");
            }}
          >
            Strike
          </Button>
        )}
      </div>
    </Panel>
  );
}

/**
 * What the disclosure admits is known: each statement is prior art an examiner
 * can rely on, so the analysis reads it as it reads an uploaded document. The
 * attorney accepts an entry, or strikes it, and a struck entry is not read.
 */
export function AdmittedArt({
  entries,
  busy,
  onRule,
}: {
  entries: readonly AdmittedEntryResponse[];
  busy: boolean;
  onRule: (entryId: string, ruling: AdmissionRuling) => void;
}) {
  if (entries.length === 0) return null;
  return (
    <section className="mt-3.5 grid gap-2" data-testid="admitted-art">
      <MicroLabel>Admitted in the disclosure</MicroLabel>
      <ul className="grid gap-1.5">
        {entries.map((entry) => (
          <AdmittedRow
            key={entry.entry_id}
            entry={entry}
            busy={busy}
            onRule={onRule}
          />
        ))}
      </ul>
    </section>
  );
}
